import * as THREE from 'three'
import { useRef } from 'react'
import { Canvas, useFrame, useThree } from '@react-three/fiber'
import { useIntersect, ScrollControls, Scroll, useScroll, Instances, Instance } from '@react-three/drei'
import * as Tone from 'tone'
import allColors from './Colors'

let randI = (i) => Math.floor(i * fxrand())
let rand = (arr) => arr[randI(arr.length)]
let ci = randI(allColors.length)
let colors = allColors[ci]
let bg = colors.shift()
let accent = rand(colors)

let scrollDistance = 7 + randI(15)
let speed = rand([0.5,0.25,1,1,1,1,1.5,2])
window.$fxhashFeatures = { stages: scrollDistance, speed }
let padLength = scrollDistance * 2
Tone.Transport.bpm.value = 10
let mainGains = []
let pads = new Array(padLength).fill().map(() => {
  let am = new Tone.AMSynth({
    "harmonicity": 1,
    "oscillator": {
      "type": "amsine2",
      "modulationType": "sine",
      "harmonicity": rand([1.01, 1.02, 1.01, 1.015, 1.0175, 1.0125, 1.01, 1.002])
    },
    "envelope": {
      "attack": '32n',
      "decay": '1m',
      "sustain": 0.5,
      "release": '16nn'
    },
    "modulation": {
      "volume": rand([3, 5, 8, 10, 15]),
      "type": "amsine2",
      "modulationType": "sine",
      "harmonicity": rand([4, 8, 12, 16])
    },
    "modulationEnvelope": {
      "attack": '32n',
      "decay": 0.2,
      "sustain": 0.5,
      "release": '4n'
    }
  })
  let mainGain = new Tone.Gain(0).toDestination()
  mainGains.push(mainGain)
  let delay = new Tone.PingPongDelay(rand(['32t','16n']), 0.8).toDestination()
  am.connect(new Tone.Gain(0.04).connect(mainGain.connect(delay)))
  am.connect(new Tone.Gain(0.2).connect(new Tone.Panner((-10 + randI(20)) * 0.1).connect(mainGain)))
  return am
})
let positions = []
let mat = new THREE.MeshBasicMaterial({
  color: accent
})

let geo = new THREE.CircleBufferGeometry(1, 128, 128)
let box = new THREE.BoxBufferGeometry(0.1, 1, 1)
let notes = new Array(padLength).fill().map(() => rand(['C', 'D', 'E', 'G', 'A']) + rand([2, 2, 2, 3,3,3, 4]))
let scales = new Array(padLength).fill().map(() => 0.1 + ((1+randI(10)) * 0.05))
let volumes = new Array(padLength).fill().map(() => 0.2 + (randI(35) * 0.01))
let visible = new Array(padLength).fill().map(() => false)
let offsetsSpeed = new Array(padLength).fill().map(() => ((1 + randI(40)) * 0.005*speed))
let offsets = new Array(padLength).fill().map((_,i) => (Math.PI*rand([0.5,0.75,1,2])))
let offsetsScale = new Array(padLength).fill().map(() => (3 + randI(40)))
let offsetsRows = new Array(padLength).fill().map(() => rand([1,2,3+randI(12)]))
let rots = new Array(padLength).fill().map(() => rand([0,Math.PI*0.5]))
let sides = new Array(padLength).fill().map(() => rand(true,false))

let wi = 1
let hi = 1

let xs = new Array(padLength).fill().map((_, i) => (((0.95) / padLength) * i) - ((0.95) / padLength) * (padLength * 0.5))
let ys = new Array(padLength).fill().map((_, i) => randI(190) * scrollDistance * 0.005)
ys[randI(padLength)] = 0
function All() {
  const { width, height } = useThree((state) => state.viewport)
  wi = width
  hi = height
  positions = new Array(padLength).fill().map((_, i) => [
    xs[i] * wi,
    ys[i] * hi,
  ])
  return (
    <Scroll>
      {new Array(padLength).fill().map((_, i) => <Item i={i} key={i} />)}
      <Instances geometry={box} material={mat}>
        {new Array(padLength).fill().map((_, i) => <Lines i={i} key={i} />)}
      </Instances>
    </Scroll>
  )
}
function Item({ i }) {
  const playing = useRef(false)
  const ref = useIntersect((isVisible) => (visible[i] = isVisible))
  useFrame((s, dt) => {
    if (visible[i]) {
      if (!playing.current) {
        pads[i].triggerAttack(notes[i], '+0.0000001', volumes[i])
        playing.current = true
      }
    } else {
      pads[i].triggerRelease()
      playing.current = false
    }
    if (started) {
      offsets[i] += dt
    }
    ref.current.position.y = Math.sin((offsets[i] * offsetsSpeed[i])) * offsetsScale[i]
  })
  return (
    <group position={[positions[i][0], -positions[i][1], 0]} rotation={[0, 0, rots[i]]}>
      <mesh ref={ref} scale={scales[i]*0.04*wi} geometry={geo} material={mat} />
    </group>
  )
}
function Line({ i,j, animated }) {
  const ref = useRef();
  useFrame(() => {
    if(animated){
      ref.current.scale.y = Math.sin((offsets[i] * offsetsSpeed[i])) * offsetsScale[i]*2
    }
  })
  return (
    <Instance ref={ref} position={[j*0.065 * (sides[i] ? 1 : -1),0,0]} scale={[0.005*wi,offsetsScale[i]*2,1]} />
  )
}
function Lines({ i }) {
  return (
    <group position={[positions[i][0], -positions[i][1], 0]} rotation={[0, 0, rots[i]]}>
      {new Array(offsetsRows[i]).fill().map((_,j) => 
        <Line i={i} j={j+1} key={j} animated={true}  />
        )}
        <Line i={i} j={0} animated={false} />
    </group>
  )
}
let started = false
let togglable = true
const click = () => {
  if (togglable) {
    if (!started) {
      Tone.start();
      Tone.Transport.start()
      started = true
      mainGains.forEach((g) => g.gain.linearRampTo(1, 0.5));
      setTimeout(() => (togglable = true), 500)
    } else {
      started = false
      setTimeout(() => (togglable = true), 500)
    }
    togglable = false
  }
}
export const App = () => (
  <div id="main" onClick={() => click()} >
    <Canvas flat={true} linear={true} orthographic camera={{ zoom: 80 }} gl={{ alpha: false, antialias: true, stencil: false, depth: false }} dpr={[1, 1.5]}>
      <color attach="background" args={[bg]} />
      <ScrollControls distance={0.75} damping={10} pages={scrollDistance}>
        <All />
      </ScrollControls>
    </Canvas>
  </div>
)
console.log('%c ⬤⬤ @ramioso 2022-09-15 ⬤⬤', 'color: orange');
